/**
 * Locale-aware external tokenizer for the rYaml Lezer grammar.
 * Handles ALL token types (Keyword, Modifier, NotText, etc.)
 * and reads from a mutable keyword map that can be updated at runtime
 * when the locale changes.
 * @module locale-tokenizer
 */

import { ExternalTokenizer } from "@lezer/lr";
import {
	BackgroundKeyword,
	BackgroundModifier,
	Keyword,
	Modifier,
	NotText,
	StackModifier,
} from "./rYaml.terms.js";

// ── Token categories ────────────────────────────────────────────────

// English keywords grouped by grammar category (without colon)
const KEYWORD_NAMES = new Set(["*", "**", "#", "##", "box", "columnTitle", "form", "line", "link", "read", "title", "subtitle", "supertitle"]);
const NOT_TEXT_NAMES = new Set(["audio", "blank", "button", "chart", "image", "loop", "map", "pause", "video"]);
const STACK_MOD_NAMES = new Set(["autoplay", "autospace", "filter", "moveBy", "storyName"]);
const BACKGROUND_NAME = "background";

// Modifiers: everything in the @tokens Modifier block (without colon)
const MODIFIER_NAMES = new Set([
	"~", "across", "align", "anchor", "animate", "animateStart",
	"backlink", "blur", "box-color", "box-corner", "border-color",
	"border-thickness", "caption", "color", "columns", "corner-radius",
	"decoration", "delay", "down", "effect", "font", "hideOn", "toPage",
	"height", "line-height", "mailto", "mute", "opacity", "padding",
	"padding-bottom", "padding-left", "padding-right", "padding-top",
	"position", "repeat", "scroll", "size", "style", "tap", "url",
	"weight", "width", "shadow",
]);

/**
 * Determine the token ID for a given English keyword/modifier name (without colon).
 */
function getTokenId(englishName: string, isIndented: boolean): number {
	if (isIndented) {
		if (englishName === BACKGROUND_NAME) return BackgroundModifier;
		if (MODIFIER_NAMES.has(englishName)) return Modifier;
		// Modifiers can only appear indented
		return -1;
	}
	// Line-start tokens
	if (englishName === BACKGROUND_NAME) return BackgroundKeyword;
	if (KEYWORD_NAMES.has(englishName)) return Keyword;
	if (NOT_TEXT_NAMES.has(englishName)) return NotText;
	if (STACK_MOD_NAMES.has(englishName)) return StackModifier;
	return -1;
}

// ── Token entry: maps a locale string to its token ID ───────────────

interface TokenEntry {
	/** The locale keyword string WITH colon (e.g. "titre:" or "title:") */
	text: string;
	/** The token type ID */
	tokenId: number;
	/** Whether this is an indented token (modifier) */
	indented: boolean;
}

// ── Mutable token map ───────────────────────────────────────────────

/**
 * All tokens the tokenizer should recognize, grouped by first character
 * for fast lookup. Rebuilt when locale changes.
 */
let lineStartTokens: TokenEntry[] = [];
let indentedTokens: TokenEntry[] = [];
let maxLineStartLen = 0;
let maxIndentedLen = 0;

/**
 * Build the token lists from English keywords plus locale translations.
 */
function buildTokenLists(
	localeKeywords?: Record<string, string>,
	localeModifiers?: Record<string, string>,
): void {
	const lineStart: TokenEntry[] = [];
	const indented: TokenEntry[] = [];

	// Always include English keywords
	for (const name of KEYWORD_NAMES) {
		const text = `${name}:`;
		lineStart.push({ text, tokenId: Keyword, indented: false });
	}
	for (const name of NOT_TEXT_NAMES) {
		const text = `${name}:`;
		lineStart.push({ text, tokenId: NotText, indented: false });
	}
	for (const name of STACK_MOD_NAMES) {
		const text = `${name}:`;
		lineStart.push({ text, tokenId: StackModifier, indented: false });
	}
	// background: at line start
	lineStart.push({ text: "background:", tokenId: BackgroundKeyword, indented: false });

	// English modifiers (indented)
	for (const name of MODIFIER_NAMES) {
		const text = `${name}:`;
		indented.push({ text, tokenId: Modifier, indented: true });
	}
	// background: as indented modifier
	indented.push({ text: "background:", tokenId: BackgroundModifier, indented: true });

	// Add locale keyword translations
	if (localeKeywords) {
		for (const [englishName, localeName] of Object.entries(localeKeywords)) {
			if (localeName === englishName) continue; // skip identity mappings
			const tokenId = getTokenId(englishName, false);
			if (tokenId >= 0) {
				lineStart.push({ text: `${localeName}:`, tokenId, indented: false });
			}
		}
	}

	// Add locale modifier translations
	if (localeModifiers) {
		for (const [englishName, localeName] of Object.entries(localeModifiers)) {
			if (localeName === englishName) continue;
			const tokenId = getTokenId(englishName, true);
			if (tokenId >= 0) {
				indented.push({ text: `${localeName}:`, tokenId, indented: true });
			}
		}
		// background as modifier in locale
		if (localeModifiers.background && localeModifiers.background !== BACKGROUND_NAME) {
			indented.push({ text: `${localeModifiers.background}:`, tokenId: BackgroundModifier, indented: true });
		}
	}

	// Sort by length descending so longer matches take priority
	lineStart.sort((a, b) => b.text.length - a.text.length);
	indented.sort((a, b) => b.text.length - a.text.length);

	lineStartTokens = lineStart;
	indentedTokens = indented;
	maxLineStartLen = lineStart.reduce((max, e) => Math.max(max, e.text.length), 0);
	maxIndentedLen = indented.reduce((max, e) => Math.max(max, e.text.length), 0);
}

// Initialize with English defaults
buildTokenLists();

/**
 * Update the token map for a new locale. Call this when locale changes.
 * Pass undefined/empty to reset to English only.
 */
export function setLocaleTokenMap(
	localeKeywords?: Record<string, string>,
	localeModifiers?: Record<string, string>,
): void {
	buildTokenLists(localeKeywords, localeModifiers);
}

// ── External tokenizer ──────────────────────────────────────────────

/**
 * Try to match a token at the current input position.
 * Returns the matched TokenEntry or null.
 */
function tryMatch(
	input: { peek(offset: number): number },
	entries: TokenEntry[],
	maxLen: number,
): TokenEntry | null {
	// Read ahead up to maxLen characters
	const chars: number[] = [];
	for (let i = 0; i < maxLen; i++) {
		const ch = input.peek(i);
		if (ch === -1 || ch === 10) break; // EOF or newline
		chars.push(ch);
	}

	const text = String.fromCharCode(...chars);

	for (const entry of entries) {
		if (text.startsWith(entry.text)) {
			return entry;
		}
	}
	return null;
}

export const locale_tokenizer = new ExternalTokenizer((input) => {
	// Check if we are at the start of a line (or start of file)
	const prev = input.peek(-1);
	if (prev !== 10 /* newline */ && prev !== -1 /* start of file */) {
		return; // Not at line start — nothing to tokenize
	}

	// Check for indentation (2+ spaces = modifier context)
	const first = input.peek(0);
	const second = input.peek(1);
	const isIndented = first === 32 /* space */ && second === 32 /* space */;

	if (isIndented) {
		// Count spaces
		let spaces = 0;
		while (input.peek(spaces) === 32) spaces++;

		// Try to match an indented token after the spaces
		// We need to peek past the spaces
		const chars: number[] = [];
		for (let i = 0; i < maxIndentedLen; i++) {
			const ch = input.peek(spaces + i);
			if (ch === -1 || ch === 10) break;
			chars.push(ch);
		}
		const text = String.fromCharCode(...chars);

		for (const entry of indentedTokens) {
			if (text.startsWith(entry.text)) {
				// Advance past spaces + token text
				input.advance(spaces + entry.text.length);
				input.acceptToken(entry.tokenId);
				return;
			}
		}
	} else {
		// Line-start token
		const match = tryMatch(input, lineStartTokens, maxLineStartLen);
		if (match) {
			input.advance(match.text.length);
			input.acceptToken(match.tokenId);
			return;
		}
	}
});
